<template>
  <div class="flex flex-col gap-4">
    <div class="flex flex-row gap-4">
      <JournalAnalysisProgress
        :surveys="surveys"
        :scale="scale"
        :selectable="selectable"
        v-model:selected-survey-id="selectedSurveyId"
      />
    </div>
    <div class="flex flex-row gap-4">
      <BaseCard type="filled" class="w-full p-4 pt-3">
        <div class="mb-2 flex flex-row items-center justify-between">
          <div class="flex shrink-0 flex-row items-center gap-2">
            <BaseMenu
              v-if="selectedSurvey"
              :items="
                surveys.map((survey) => ({
                  id: survey.id,
                  title: useDisplayDate(survey.date).value,
                  leadingIcon: selectedSurveyId === survey.id ? 'check' : undefined,
                  callback: () => {
                    selectedSurveyId = survey.id
                  },
                }))
              "
              v-slot="{ open }"
              ><BaseChip type="filter" icon="calendar_today" :menu="open ? 'open' : 'close'">{{
                useDisplayDate(selectedSurvey.date, { dayName: 'short' })
              }}</BaseChip></BaseMenu
            >

            <div v-if="!props.displayInProfile" class="mt-1 ml-2 flex w-40 grow flex-col">
              <BaseLinearProgressIndictor
                class="grow"
                :percentage="percentageOfParticipantsForSelectedSurvey"
              />
              <span class="text-label-small text-on-surface-variant">
                {{ actualParticipantsIdsForSelectedSurvey.length }}/{{
                  selectedSurvey?.participantIds.length
                }}
                {{ $t('common.finished') }}
              </span>
            </div>
          </div>

          <div v-if="selectedSurvey" class="flex flex-row items-center">
            <TendencyBage
              v-if="
                selectedSurvey.tendency != undefined &&
                actualParticipantsIdsForSelectedSurvey.length > 0
              "
              :tendency="selectedSurvey.tendency"
              size="medium"
              class="mr-2"
            />

            <span
              v-if="
                latestValue.average != null && actualParticipantsIdsForSelectedSurvey.length > 0
              "
              class="text-headline-large text-on-secondary-container z-10 font-bold"
              >{{ useDisplayPercentage(selectedSurvey.average!) }}</span
            >
          </div>
          <BaseConnectedButtonGroup
            v-if="!displayInProfile"
            v-model="mode"
            class="ml-4"
            type="icon"
            color="outlined"
            size="XS"
            shape="square"
            :items="[
              { label: 'Mitglieder', value: 'by_members', icon: 'group' },
              { label: 'Fragen', value: 'by_questions', icon: 'splitscreen' },
            ]"
          />
        </div>
        <div v-if="memberValuesForSelectedSurvey" class="flex flex-col">
          <template v-if="!displayInProfile">
            <ul v-if="mode === 'by_members'" class="flex flex-col gap-0.5">
              <li
                v-for="value in memberValuesForSelectedSurvey"
                :key="value.memberId"
                class="first:*:rounded-t-large last:*:rounded-b-large"
              >
                <JournalAnalysisMemberItem
                  :tendency="value.tendency"
                  :average="value.average"
                  :feedbacks="value.feedbacks"
                  :member="value.member"
                />
              </li>
            </ul>
            <ul v-else class="flex flex-col gap-0.5">
              <li
                v-for="value in questionValuesForSelectedSurvey"
                :key="value.question.id"
                class="first:*:rounded-t-large last:*:rounded-b-large"
              >
                <JournalAnalysisQuestionItem
                  :average="value.average"
                  :question="value.question"
                  :feedbacks="
                    value.feedbacks.map((feedback) => ({
                      id: feedback.id,
                      value: feedback.value,
                      text: feedback.text,
                      member: feedback.member,
                    }))
                  "
                  :tendency="value.tendency"
                  :scale="props.scale"
                />
              </li>
            </ul>
            <p
              v-if="memberValuesForSelectedSurvey.length === 0"
              class="text-body-small text-on-surface-variant p-4"
            >
              Keine Antworten fuer dieses Datum vorhanden.
            </p>
          </template>
          <template v-else>
            <JournalAnalysisMemberItemBody
              :member="memberValuesForSelectedSurvey[0].member"
              :feedbacks="
                memberValuesForSelectedSurvey.flatMap((value) =>
                  value.feedbacks.map((feedback) => ({
                    id: feedback.id,
                    questionId: feedback.question.id,
                    value: feedback.value,
                    text: feedback.text,
                  })),
                )
              "
              :questions="questions"
              class="[&>li]:bg-surface-container-lowest [&>li]:rounded-extra-small [&>li]:first:rounded-t-large [&>li]:last:rounded-b-large gap-0.5"
            />
          </template>
        </div>
      </BaseCard>
    </div>
  </div>
</template>

<script setup lang="ts">
  import BaseCard from '../../../core/components/base/card/BaseCard.vue'
  import { useDisplayDate } from '../../../core/composables/use-display-date'
  import { useDisplayPercentage } from '../../../core/composables/use-display-percentage'
  import BaseMenu from '@/core/components/base/menu/BaseMenu.vue'
  import BaseChip from '@/core/components/base/chip/BaseChip.vue'
  import TendencyBage from '@/core/components/base/tendency-badge/TendencyBage.vue'
  import JournalAnalysisMemberItem from './JournalAnalysisMemberItem.vue'
  import JournalAnalysisQuestionItem from './JournalAnalysisQuestionItem.vue'
  import type { JournalAnalysis } from './journal-analysis'
  import type { Member } from '@/core/models/member/member'
  import { computed, ref } from 'vue'
  import { useMembersStore } from '@/stores/members.store'
  import BaseLinearProgressIndictor from '../base/linear-progress-indicator/BaseLinearProgressIndictor.vue'
  import BaseConnectedButtonGroup from '../base/button/BaseConnectedButtonGroup.vue'
  import JournalAnalysisProgress from './JournalAnalysisProgress.vue'
  import JournalAnalysisMemberItemBody from './JournalAnalysisMemberItemBody.vue'

  const props = defineProps<
    JournalAnalysis & { displayInProfile?: boolean; selectable?: boolean }
  >()

  const latestValue = computed(() => {
    return props.surveys[0]
  })

  const selectedSurveyId = ref<string | undefined>(latestValue.value?.id)
  const selectedSurvey = computed(() => {
    return props.surveys.find((survey) => survey.id === selectedSurveyId.value)
  })

  const questionMap = computed(() => {
    return new Map<string, JournalAnalysis['questions'][number]>(
      props.questions.map((question) => [question.id, question]),
    )
  })

  const memberStore = useMembersStore()
  const mode = ref<'by_members' | 'by_questions'>('by_members')

  const memberValuesForSelectedSurvey = computed(() => {
    return (
      selectedSurvey.value?.participantIds.map((participantId) => {
        const member = memberStore.getMemberById(participantId)!
        const value = props.individualValues.find(
          (value) => value.memberId === participantId && value.surveyId === selectedSurveyId.value,
        )
        return {
          memberId: participantId,
          member: member,
          tendency: value ? value.tendency : null,
          average: value ? value.average : null,
          feedbacks:
            value?.feedbacks.map((feedback) => ({
              id: feedback.questionId,
              value: feedback.value,
              text: feedback.text,
              question: questionMap.value.get(feedback.questionId)!,
            })) ?? [],
        }
      }) ?? []
    )
  })

  const actualParticipantsIdsForSelectedSurvey = computed(() => {
    return [
      ...new Set(
        memberValuesForSelectedSurvey.value
          .filter((value) => value.average != null)
          .map((value) => value.member.id),
      ),
    ]
  })

  const questionValuesForSelectedSurvey = computed(() => {
    const questionValues = new Map<
      string,
      {
        question: JournalAnalysis['questions'][number]
        tendency: number
        feedbacks: Array<{
          id: string
          value: number | null
          text: JSON | null
          member: Member
        }>
      }
    >()

    for (const question of props.questions) {
      questionValues.set(question.id, {
        question,
        tendency: 0,
        feedbacks: [],
      })
    }

    for (const value of props.individualValues.filter(
      (individualValue) => individualValue.surveyId === selectedSurveyId.value,
    )) {
      const member = memberStore.getMemberById(value.memberId)
      if (!member) {
        continue
      }

      for (const feedback of value.feedbacks) {
        const questionEntry = questionValues.get(feedback.questionId)
        if (!questionEntry) {
          continue
        }
        questionEntry.tendency += value.tendency ?? 0
        questionEntry.feedbacks.push({
          id: value.memberId,
          value: feedback.value,
          text: feedback.text,
          member,
        })
      }
    }

    return props.questions.map((question) => {
      const mappedQuestion = questionValues.get(question.id)!
      const answeredValues = mappedQuestion.feedbacks
        .map((feedback) => feedback.value)
        .filter((feedbackValue): feedbackValue is number => feedbackValue != null)
      const average =
        answeredValues.length > 0
          ? answeredValues.reduce((sum, feedbackValue) => sum + feedbackValue, 0) /
            answeredValues.length
          : null
      return {
        question: mappedQuestion.question,
        average,
        feedbacks: mappedQuestion.feedbacks,
        tendency: mappedQuestion.tendency,
      }
    })
  })

  const percentageOfParticipantsForSelectedSurvey = computed(() => {
    return (
      (actualParticipantsIdsForSelectedSurvey.value.length /
        (selectedSurvey.value?.participantIds.length ?? 0)) *
      100
    )
  })
</script>
